import { useGetDataKeyDefinitions } from "src/hooks/queries";
import { ChangeManagementPlan } from "src/types/change-management";
import {
  countPlanItems,
  describePlanCounts,
  getEvidenceLabel,
} from "./change-management-plan";
import { PlanIssuesCard } from "./PlanIssuesCard";
import { PlanOverviewCard } from "./PlanOverviewCard";
import { PlanReviewFooter } from "./PlanReviewFooter";
import { PlanSection } from "./PlanSection";
import { RecommendationCard } from "./RecommendationCard";
import { useResolvedPlanDocuments } from "./useResolvedPlanDocuments";

export const ChangeManagementPlanReview = ({
  orgId,
  plan,
}: {
  orgId: string;
  plan: ChangeManagementPlan;
}) => {
  const resolveDocument = useResolvedPlanDocuments(orgId, plan);
  const { data: dataKeyDefinitions } = useGetDataKeyDefinitions();
  const dataKeyLabels = new Map(
    dataKeyDefinitions?.map((dataKey) => [
      dataKey.key,
      dataKey.question.options?.label,
    ])
  );
  const counts = countPlanItems(plan);

  return (
    <div className="flex flex-col gap-8">
      <PlanOverviewCard plan={plan} resolveDocument={resolveDocument} />
      {plan.issues.length > 0 && <PlanIssuesCard issues={plan.issues} />}
      <PlanSection
        title="Suggested changes"
        description={
          plan.recommendations.length ? describePlanCounts(counts) : undefined
        }
      >
        {plan.recommendations.length ? (
          plan.recommendations.map((recommendation, index) => (
            <RecommendationCard
              key={index}
              recommendation={recommendation}
              resolvedDocument={
                recommendation.document_id && recommendation.version_id
                  ? resolveDocument(
                      recommendation.document_id,
                      recommendation.version_id
                    )
                  : null
              }
              getDataKeyLabel={(dataKey) => dataKeyLabels.get(dataKey) || null}
              getEvidenceLabel={(sourceUrl) =>
                getEvidenceLabel(sourceUrl, plan)
              }
            />
          ))
        ) : (
          <div className="flex flex-col items-center gap-2 rounded border border-solid border-black/[.12] bg-white px-6 py-12 text-center text-black/60">
            <h3 className="m-0 text-[16px] font-semibold leading-[1.5] text-black/[.87]">
              No document changes recommended yet
            </h3>
            <p className="m-0 max-w-[60ch]">
              {plan.issues.length
                ? "The agent could not tie this change to specific documents. Resolve the open issues above and ask it to revise the plan."
                : "The agent found no documents that this change affects."}
            </p>
          </div>
        )}
        <PlanReviewFooter changeCount={counts.changes} />
      </PlanSection>
    </div>
  );
};
